/* eslint-disable camelcase */
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';

export type AuthMethodsSettings = {
  email_otp_enabled: boolean;
  otp_mandatory: boolean;
};

type EnableOtpRequest = {
  emailAddress: string;
  enableEmailOtp: boolean;
};

type OtpCodeExpiration = {
  otpCodeExpirationMinutes: number;
};

type EnableOtpVerificationRequest = {
  otp: string;
};

type SendOtpRequest = {
  emailAddress?: string;
};

export const TwoFactorAuthApi = createApi({
  reducerPath: 'TwoFactorAuthApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['email-otp'],
  endpoints: builder => ({
    getAuthMethods: builder.query<AuthMethodsSettings, void>({
      query: () =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions.getAuthMethods,
          {}
        ),
      providesTags: ['email-otp'],
      extraOptions: {
        maxRetries: 0,
      },
    }),
    updateAuthMethods: builder.mutation<
      AuthMethodsSettings,
      AuthMethodsSettings
    >({
      query: data =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions.updateAuthMethods,
          { data }
        ),
      invalidatesTags: ['email-otp'],
      extraOptions: {
        maxRetries: 0,
      },
    }),
    enableEmailOtp: builder.mutation<OtpCodeExpiration, EnableOtpRequest>({
      query: data =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions.otpEnablingRequest,
          { data }
        ),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    enableEmailOtpVerification: builder.mutation<
      void,
      EnableOtpVerificationRequest
    >({
      query: data =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions.otpEnablingVerification,
          { data }
        ),
      invalidatesTags: ['email-otp'],
      extraOptions: {
        maxRetries: 0,
      },
    }),
    sendOtp: builder.mutation<OtpCodeExpiration, SendOtpRequest>({
      query: data =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions.sendOtpRequest,
          { data }
        ),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    getUserAuthMethodEmailOtp: builder.query<{ enabled: boolean }, void>({
      query: () =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions
            .getUserAuthMethodEmailOtp,
          {}
        ),
      providesTags: ['email-otp'],
      extraOptions: {
        maxRetries: 0,
      },
    }),
    deleteUserAuthMethodEmailOtp: builder.mutation<void, void>({
      query: () =>
        apiResource(
          B2ChatClient.resources.twoFactorAuth.actions
            .deleteUserAuthMethodEmailOtp,
          {}
        ),
      invalidatesTags: ['email-otp'],
      extraOptions: {
        maxRetries: 0,
      },
    }),
  }),
});

export const {
  useGetAuthMethodsQuery,
  useUpdateAuthMethodsMutation,
  useSendOtpMutation,
  useEnableEmailOtpMutation,
  useEnableEmailOtpVerificationMutation,
  useGetUserAuthMethodEmailOtpQuery,
  useLazyGetUserAuthMethodEmailOtpQuery,
  useDeleteUserAuthMethodEmailOtpMutation,
} = TwoFactorAuthApi;
